import { flexRender, Table } from '@tanstack/react-table';
import { FC, Fragment, useState, useRef } from 'react';
import { classNames } from 'primereact/utils';
import { MedicalRecordsListTableRow } from '../hooks';
import { DataTableHeaderFilter, DataTableProgressBarRow, DataTableSortingIndicator } from '@repo/atomic-components';
import { MedicalRecordsFiltersResponse } from '@/api';
import { useTableSpacerWidth } from '@/hooks';
import { UploadedByHeaderFilter } from './UploadedByHeaderFilter';

type FilterOptionKey = keyof MedicalRecordsFiltersResponse;

const isFilterOptionKey = (key: string): key is FilterOptionKey => {
  return ['caseStatus', 'checkedBy', 'documentStatus', 'providerTypes', 'providers', 'uploadedBy'].includes(key);
};

export interface UploadedDocumentsTableProps {
  table: Table<MedicalRecordsListTableRow>;
  filterOptions: Record<string, string[]>;
  selectedFilterOptions: Record<string, string[]>;
  handleFilterChange: (columnId: string, values: string[]) => void;
  selectedUploadDateRange: { minUploadDate?: string; maxUploadDate?: string };
  handleUploadedByFilterChange: (
    values: string[],
    dateRange: { minUploadDate?: string; maxUploadDate?: string },
  ) => void;
}

export const MedicalRecordsListTable: FC<UploadedDocumentsTableProps> = ({
  table,
  filterOptions,
  selectedFilterOptions,
  handleFilterChange,
  selectedUploadDateRange,
  handleUploadedByFilterChange,
}) => {
  const [filterAnchor, setFilterAnchor] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const rows = table.getRowModel().rows;

  useTableSpacerWidth(containerRef, tableRef, [rows]);

  return (
    <div ref={containerRef} className="h-full w-full">
      <table
        ref={tableRef}
        className={classNames('min-w-full border-none', {
          'h-full': rows.length === 0,
        })}
      >
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className={classNames('sticky top-0 z-10 h-12 max-h-12 min-h-12')}>
              {headerGroup.headers.map((header, headerIndex) => (
                <Fragment key={header.id}>
                  {/* Header underline uses an inset `box-shadow` instead of
                      a `border-b` because Chrome / Safari drop the bottom
                      border on `<th>` cells once the parent `<tr>` enters
                      sticky positioning. `box-shadow` paints independently
                      of the table border model and stays visible through
                      the sticky transition. The colour matches the inter-
                      row dividers (`fletchers-gray-110` = #E1E1E0). */}
                  <th className="bg-fletchers-white px-2 py-1" style={{ boxShadow: 'inset 0 -1px 0 #E1E1E0' }}>
                    <div className="flex items-center justify-start">
                      <div
                        onClick={header.column.getToggleSortingHandler?.()}
                        className={classNames(
                          'flex items-center justify-center',
                          'text-fletchers-gray-175 text-[14px]/[20px] font-[600]',
                          {
                            'cursor-pointer': header.column.getCanSort?.(),
                          },
                        )}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </div>

                      {/* Sorting indicator */}
                      {header.column.getCanSort?.() && (
                        <div onClick={header.column.getToggleSortingHandler?.()} className="cursor-pointer">
                          <DataTableSortingIndicator
                            isSorted={header.column.getIsSorted?.()}
                            containerClassName="text-[#D9D9D9]"
                          />
                        </div>
                      )}

                      {/* Filter component (left of header text) */}
                      {header.column.getCanFilter?.() &&
                        isFilterOptionKey(header.column.id) &&
                        filterOptions[header.column.id] &&
                        (header.column.id === 'uploadedBy' ? (
                          <UploadedByHeaderFilter
                            columnId={header.column.id}
                            filterOptions={filterOptions[header.column.id] || []}
                            currentFilterValue={selectedFilterOptions[header.column.id] || []}
                            currentDateRange={selectedUploadDateRange}
                            onApply={handleUploadedByFilterChange}
                            filterAnchor={filterAnchor}
                            setFilterAnchor={setFilterAnchor}
                          />
                        ) : (
                          <DataTableHeaderFilter
                            columnId={header.column.id}
                            filterOptions={filterOptions[header.column.id] || []}
                            currentFilterValue={selectedFilterOptions[header.column.id] || []}
                            setFilterValue={(value) => handleFilterChange(header.column.id, value)}
                            filterAnchor={filterAnchor}
                            setFilterAnchor={setFilterAnchor}
                          />
                        ))}
                    </div>
                  </th>
                  {/* Spacer header cell — skip after select checkbox column (index 0) and last column */}
                  {headerIndex !== 0 && headerIndex !== headerGroup.headers.length - 1 && (
                    <th
                      aria-hidden
                      data-spacer
                      className="bg-fletchers-white"
                      style={{ width: 'var(--spacer-w, 100%)', boxShadow: 'inset 0 -1px 0 #E1E1E0' }}
                    />
                  )}
                </Fragment>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <Fragment key={row.id}>
                <tr
                  key={row.id}
                  className={classNames(
                    'group h-[60px] max-h-[60px] min-h-[60px]',
                    'bg-fletchers-white border-fletchers-gray-110 border-b last:border-b-0',
                    'hover:bg-fletchers-gray-50',
                  )}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => (
                    <Fragment key={cell.id}>
                      <td className={classNames('border-none px-2 py-1', 'text-[14px]/[20px] font-[400]')}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext()) as React.ReactNode}
                      </td>
                      {/* Spacer body cell — skip after select checkbox column (index 0) and last column */}
                      {cellIndex !== 0 && cellIndex !== row.getVisibleCells().length - 1 && (
                        <td
                          aria-hidden
                          data-spacer
                          className="border-none"
                          style={{ width: 'var(--spacer-w, 100%)' }}
                        />
                      )}
                    </Fragment>
                  ))}
                </tr>

                {row.original.progressBars && (
                  <DataTableProgressBarRow
                    colSpan={table.getAllColumns().length}
                    progressBars={row.original.progressBars}
                  />
                )}
              </Fragment>
            ))
          ) : (
            <tr>
              <td colSpan={table.getAllColumns().length * 2}>
                <div
                  className={classNames(
                    'flex items-center justify-center',
                    'border-none px-2 py-1',
                    'text-[14px]/[20px] font-[400]',
                  )}
                >
                  No results found
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
